import { Link } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Server,
  Activity,
  AlertTriangle,
  BookOpen,
  Fingerprint,
  FolderSync,
  LifeBuoy,
  Send,
  Settings,
  KeyRound,
  CalendarCheck,
  CalendarClock,
  Sparkles,
  Rocket,
  Cpu,
  FileWarning,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useDraftNotificationCount } from '@/hooks/useMail';
import { useContractChangesCount } from '@/hooks/useContractChanges';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Optionales Element rechtsbündig (z.B. Entwurfs-Zähler). */
  trailing?: ReactNode;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="size-4" /> },
  { to: '/customers', label: 'Kunden', icon: <Users className="size-4" /> },
  {
    to: '/contracts/changes',
    label: 'Vertragsänderungen',
    icon: <FileWarning className="size-4" />,
    trailing: <ContractChangesBadge />,
  },
  { to: '/systems', label: 'Systeme', icon: <Server className="size-4" /> },
  { to: '/systems/kernel-status', label: 'Kernel-Stand', icon: <Cpu className="size-4" /> },
  {
    to: '/systems/zugangsmigration',
    label: 'Zugangsmigration',
    // Fingerabdruck statt Schlüssel: KeyRound hängt schon an "Session"
    // (Bitwarden-Sitzung), und zwei gleiche Symbole in einer Leiste sind
    // keine Navigation, sondern ein Suchspiel.
    icon: <Fingerprint className="size-4" />,
  },
  {
    to: '/inventory/import',
    label: 'Inventar-Abgleich',
    icon: <FolderSync className="size-4" />,
  },
  {
    to: '/maintenance-plans',
    label: 'Wartungspläne',
    icon: <CalendarClock className="size-4" />,
  },
  {
    to: '/maintenance/due',
    label: 'Fällige Wartungen',
    icon: <CalendarCheck className="size-4" />,
  },
  { to: '/routines', label: 'Routinen', icon: <BookOpen className="size-4" /> },
  { to: '/ai/suggest', label: 'KI-Vorschlag', icon: <Sparkles className="size-4" /> },
  { to: '/sessions', label: 'Session', icon: <KeyRound className="size-4" /> },
  { to: '/runs', label: 'Runs', icon: <Activity className="size-4" /> },
  { to: '/runs/rollout', label: 'Rollout', icon: <Rocket className="size-4" /> },
  {
    to: '/notifications',
    label: 'Postausgang',
    icon: <Send className="size-4" />,
    trailing: <DraftCountBadge />,
  },
  { to: '/drift', label: 'Drift', icon: <AlertTriangle className="size-4" /> },
  { to: '/settings', label: 'Einstellungen', icon: <Settings className="size-4" /> },
  { to: '/howto', label: 'Howto', icon: <LifeBuoy className="size-4" /> },
];

/**
 * Anzahl offener Mail-Entwürfe als Sidebar-Badge. Rendert nichts, solange
 * keine Daten da sind oder keine Entwürfe offen sind (kein "0"-Rauschen).
 */
function DraftCountBadge(): JSX.Element | null {
  const count = useDraftNotificationCount();
  if (!count) return null;
  return (
    <span
      aria-label={`${count} offene Entwürfe`}
      className="ml-auto rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary"
    >
      {count}
    </span>
  );
}

/**
 * Offene Vertragsänderungen als Sidebar-Badge (Fall Fortis Unum). Rendert
 * nichts, solange nichts offen ist - dieselbe Regel wie beim Entwurfs-Zähler
 * darüber: eine dauerhafte "0" ist Rauschen, kein Signal.
 */
function ContractChangesBadge(): JSX.Element | null {
  const count = useContractChangesCount();
  if (!count) return null;
  return (
    <span
      aria-label={`${count} offene Vertragsänderungen`}
      className="ml-auto rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning"
    >
      {count}
    </span>
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps): JSX.Element {
  return (
    <>
      {/* Mobile-Backdrop */}
      <div
        className={[
          'fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden',
          open ? 'block' : 'hidden',
        ].join(' ')}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-surface',
          'transition-transform md:static md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-label="Hauptnavigation"
      >
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <span className="font-semibold tracking-tight">ITKFM Wartung</span>
          <button
            type="button"
            onClick={onClose}
            className="md:hidden rounded-md p-1 text-muted hover:bg-surface-2"
            aria-label="Sidebar schließen"
          >
            <X className="size-4" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onClose}
                  activeOptions={{ exact: item.to === '/' }}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-text hover:bg-surface-2"
                  activeProps={{
                    className:
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm bg-primary/10 text-primary font-medium',
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                  {item.trailing}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-border p-3 text-xs text-muted">
          ITKFM Wartungstool · v0.1.0
        </div>
      </aside>
    </>
  );
}
